import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, Outlet } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";
import { AuthProvider, LoginCallback, ProtectedRoute } from "@/auth";
import { getOptionalConfig } from "@/config";
import { createStoreRoot } from "@/store/createStoreRoot";
import { createCompositionRootUI } from "@/ui/createCompositionRootUI";
import { ThemeProvider } from "@/ui/ThemeProvider";
import "@/ui/global.css";

const rumApplicationId = getOptionalConfig("VITE_DD_RUM_APPLICATION_ID");
const rumClientToken = getOptionalConfig("VITE_DD_RUM_CLIENT_TOKEN");

if (rumApplicationId && rumClientToken) {
    datadogRum.init({
        applicationId: rumApplicationId,
        clientToken: rumClientToken,
        site: "datadoghq.eu",
        service: "proda-finance-frontend",
        env: getOptionalConfig("VITE_DD_ENV"),
        version: getOptionalConfig("VITE_APP_VERSION"),
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask",
        allowedTracingUrls: [
            {
                match: /\/api\//,
                propagatorTypes: ["datadog" as const],
            },
        ],
        plugins: [reactPlugin({ router: true })],
    });
}

// Create store
const store = createStoreRoot();

// Create composition root with all views
const { routes: appRoutes } = createCompositionRootUI({ store });

// Wrap app routes with ProtectedRoute
const protectedAppRoutes = appRoutes.map((route) => ({
    ...route,
    element: <ProtectedRoute>{route.element}</ProtectedRoute>,
}));

// Root component that provides Auth0 context
// Auth0Provider needs to be inside RouterProvider because it uses useNavigate
function RootWithAuth() {
    return (
        <AuthProvider>
            <Outlet />
        </AuthProvider>
    );
}

// Create router with auth routes
const router = createBrowserRouter([
    {
        element: <RootWithAuth />,
        children: [
            // Login callback route (public)
            {
                path: "/login/callback",
                element: <LoginCallback />,
            },
            // All app routes (protected)
            ...protectedAppRoutes,
        ],
    },
]);

// Mount application (reuse existing root when module re-executes, e.g. HMR)
const container = document.getElementById("root");
if (!container) {
    throw new Error("Root element not found");
}
declare global {
    interface Window {
        __gltbReactRoot?: ReturnType<typeof createRoot>;
    }
}
const root = window.__gltbReactRoot ?? createRoot(container);
if (!window.__gltbReactRoot) {
    window.__gltbReactRoot = root;
}
root.render(
    <StrictMode>
        <ThemeProvider>
            <RouterProvider router={router} />
        </ThemeProvider>
    </StrictMode>,
);
